import { useState, useEffect, useRef } from 'react';
import { useBusinessStore } from '../stores/business';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
}

interface ChatSession {
  sessionId: string;
  agentName: string;
  startingMessage: string | null;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export default function TestChatPage() {
  const { businesses, agents, fetchBusinesses, fetchAgents } = useBusinessStore();

  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load businesses on mount
  useEffect(() => {
    if (businesses.length === 0) {
      fetchBusinesses();
    }
  }, [businesses.length, fetchBusinesses]);

  // Load agents when business changes
  useEffect(() => {
    if (selectedBusinessId) {
      fetchAgents(selectedBusinessId);
    }
  }, [selectedBusinessId, fetchAgents]);

  // Set default selections
  useEffect(() => {
    if (businesses.length > 0 && !selectedBusinessId) {
      setSelectedBusinessId(businesses[0].id);
    }
  }, [businesses, selectedBusinessId]);

  useEffect(() => {
    if (agents.length > 0 && !selectedAgentId) {
      const defaultAgent = agents.find((a) => a.isDefault) || agents[0];
      setSelectedAgentId(defaultAgent?.id || null);
    }
  }, [agents, selectedAgentId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when session starts
  useEffect(() => {
    if (session) {
      inputRef.current?.focus();
    }
  }, [session]);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  const startChat = async () => {
    if (!selectedAgentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/web-assistant/chat/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentId: selectedAgentId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to start chat session');
      }

      const data = await response.json();
      const sessionData = data.data;

      setSession({
        sessionId: sessionData.sessionId,
        agentName: sessionData.agent.name,
        startingMessage: sessionData.agent.startingMessage,
      });

      // Add starting message if present
      const initialMessages: ChatMessage[] = [];
      if (sessionData.messages && sessionData.messages.length > 0) {
        sessionData.messages.forEach((msg: { role: string; content: string; timestamp: number }, index: number) => {
          initialMessages.push({
            id: `msg_${index}`,
            role: msg.role as 'user' | 'assistant',
            content: msg.content,
            timestamp: msg.timestamp,
          });
        });
      } else if (sessionData.agent.startingMessage) {
        initialMessages.push({
          id: 'msg_0',
          role: 'assistant',
          content: sessionData.agent.startingMessage,
          timestamp: Date.now(),
        });
      }

      setMessages(initialMessages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start chat');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!session || !inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);
    setError(null);

    // Add user message immediately
    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const response = await fetch(`${API_BASE_URL}/web-assistant/chat/${session.sessionId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to send message');
      }

      const data = await response.json();
      const assistantMessage = data.data.message;

      // Add assistant response
      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: assistantMessage.content,
        timestamp: assistantMessage.timestamp,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Add error message to chat
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: 'system',
          content: `Error: ${err instanceof Error ? err.message : 'Failed to send message'}`,
          timestamp: Date.now(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const endChat = async () => {
    if (!session) return;

    try {
      await fetch(`${API_BASE_URL}/web-assistant/chat/${session.sessionId}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          outcome: 'user_ended',
        }),
      });
    } catch {
      // Ignore errors when ending
    }

    setSession(null);
    setMessages([]);
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="p-8 h-screen flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-white">Test Chat</h1>
        <p className="mt-1 text-surface-400">
          Test the text chat assistant with a real AI conversation
        </p>
      </div>

      {/* Main content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
        {/* Left panel - Chat controls */}
        <div className="card flex flex-col">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">Chat Controls</h2>
          </div>
          <div className="card-body flex-1 flex flex-col">
            {!session ? (
              <div className="space-y-4">
                {/* Business Selection */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Business
                  </label>
                  <select
                    value={selectedBusinessId || ''}
                    onChange={(e) => {
                      setSelectedBusinessId(e.target.value);
                      setSelectedAgentId(null);
                    }}
                    className="input"
                  >
                    <option value="">Select a business...</option>
                    {businesses.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Agent Selection */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Agent
                  </label>
                  <select
                    value={selectedAgentId || ''}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className="input"
                    disabled={!selectedBusinessId}
                  >
                    <option value="">Select an agent...</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} {a.isDefault ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedAgent && (
                  <div className="p-3 rounded-lg bg-surface-800 border border-surface-700 text-sm">
                    <p className="text-surface-300 font-medium">{selectedAgent.name}</p>
                    <p className="text-surface-500 text-xs mt-1">
                      LLM: {selectedAgent.llmProvider} / {selectedAgent.model || 'gpt-4o'}
                    </p>
                    {selectedAgent.description && (
                      <p className="text-surface-500 text-xs mt-1">{selectedAgent.description}</p>
                    )}
                  </div>
                )}

                <button
                  onClick={startChat}
                  disabled={isLoading || !selectedAgentId}
                  className="btn-primary w-full mt-4"
                >
                  {isLoading ? 'Starting...' : 'Start Chat'}
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                {/* Session info */}
                <div className="mb-4 p-3 rounded-lg bg-surface-800 border border-surface-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-2 w-2 rounded-full bg-accent-400 animate-pulse" />
                    <span className="text-sm text-accent-400">Chat Active</span>
                  </div>
                  <p className="text-surface-300 text-sm font-medium">{session.agentName}</p>
                  <p className="text-surface-500 text-xs font-mono mt-1">
                    Session: {session.sessionId.slice(0, 20)}...
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-surface-800">
                    <p className="text-2xl font-bold text-white">
                      {messages.filter((m) => m.role === 'user').length}
                    </p>
                    <p className="text-xs text-surface-500">Your messages</p>
                  </div>
                  <div className="p-3 rounded-lg bg-surface-800">
                    <p className="text-2xl font-bold text-white">
                      {messages.filter((m) => m.role === 'assistant').length}
                    </p>
                    <p className="text-xs text-surface-500">AI responses</p>
                  </div>
                </div>

                <div className="flex-1" />

                <button
                  onClick={endChat}
                  className="btn-secondary w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  End Chat
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Middle panel - Chat messages */}
        <div className="lg:col-span-2 card flex flex-col">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">Conversation</h2>
            {messages.length > 0 && (
              <span className="text-xs text-surface-500">{messages.length} messages</span>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-surface-800 mb-4">
                    <svg className="w-8 h-8 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-surface-400 text-sm">
                    {session ? 'Send a message to start the conversation' : 'Select an agent and start a chat'}
                  </p>
                </div>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-primary-600 text-white rounded-br-md'
                        : msg.role === 'system'
                        ? 'bg-yellow-500/20 text-yellow-400 rounded-bl-md'
                        : 'bg-surface-700 text-surface-100 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-1 ${
                      msg.role === 'user' ? 'text-primary-200' : 'text-surface-500'
                    }`}>
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))
            )}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-surface-700 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-surface-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 rounded-full bg-surface-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 rounded-full bg-surface-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-surface-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          {session && (
            <div className="p-4 border-t border-surface-800">
              <div className="flex gap-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="input flex-1"
                  disabled={isSending}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isSending}
                  className="btn-primary px-6"
                >
                  {isSending ? (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

